<template>
  <Timeline :value="filteredTimeLineItems" class="shipment-timeline">
    <template #marker="{ item }">
      <ShipmentTimelineMarker
        :timeline-item="item"
        :require-both-dates="requireBothDates"
      />
    </template>

    <template #content="{ item }">
      <ShipmentTimelineItem
        :timeline-item="item"
        :service-incident-types="serviceIncidentTypes"
        :disabled="disabled"
        @delete-shipment-event="emit('delete-shipment-event', $event)"
        @delete-shipment-service-incident="
          emit('delete-shipment-service-incident', $event)
        "
        @duplicate-shipment-event="onDuplicate"
        @edit-shipment-event="emit('edit-shipment-event', $event)"
        @edit-shipment-service-incident="
          emit('edit-shipment-service-incident', $event)
        "
      />
    </template>
  </Timeline>
</template>

<script lang="ts" setup>
import { computed, PropType, toRefs } from 'vue';

import { ServiceIncidentType } from '@rygen/rygen-ui/model';

import {
  ShipmentEvent,
  ShipmentEventSource,
  ShipmentServiceIncident,
  TenderRequest,
  TenderResponse,
  TimelineItem,
} from '@/model';

import ShipmentTimelineItem from '@/components/shipment/ShipmentTimelineItem.vue';
import ShipmentTimelineMarker from '@/components/shipment/ShipmentTimelineMarker.vue';
import Timeline from 'primevue/timeline';

const props = defineProps({
  shipmentTimelineItems: {
    type: Array as PropType<TimelineItem[]>,
    required: true,
  },
  serviceIncidentTypes: {
    type: Array as PropType<ServiceIncidentType[]>,
    default: () => [],
  },
  requireBothDates: {
    type: Boolean,
    default: () => false,
  },
  showEntriesWithoutLocations: {
    type: Boolean,
    default: () => false,
  },
  disabled: {
    type: Boolean,
    default: () => false,
  },
});
const {
  shipmentTimelineItems,
  serviceIncidentTypes,
  requireBothDates,
  showEntriesWithoutLocations,
  disabled,
} = toRefs(props);

const emit = defineEmits<{
  (event: 'delete-shipment-event', shipmentEvent: ShipmentEvent): void;
  (
    event: 'delete-shipment-service-incident',
    serviceIncident: ShipmentServiceIncident,
  ): void;
  (event: 'edit-shipment-event', shipmentEvent: ShipmentEvent): void;
  (
    event: 'edit-shipment-service-incident',
    serviceIncident: ShipmentServiceIncident,
  ): void;
}>();

const filteredTimeLineItems = computed(() => {
  if (!showEntriesWithoutLocations.value) {
    return shipmentTimelineItems.value.filter(
      (i) =>
        i instanceof TenderRequest ||
        i instanceof TenderResponse ||
        hasActualLocation(i),
    );
  }
  return shipmentTimelineItems.value;
});

const hasActualLocation = (
  timeLineItem: ShipmentEvent | ShipmentServiceIncident,
): Boolean => {
  return (
    !!timeLineItem.actualLocation.addressLine1 ||
    !!timeLineItem.actualLocation.city ||
    !!timeLineItem.actualLocation.state ||
    !!timeLineItem.actualLocation.postalCode ||
    !!timeLineItem.actualLocation.coordinates
  );
};

const onDuplicate = (shipmentEvent: ShipmentEvent) => {
  const newEvent = new ShipmentEvent({
    ...shipmentEvent,
    id: undefined,
    source: ShipmentEventSource.USER,
  });
  emit('edit-shipment-event', newEvent);
};
</script>

<style>
:root {
  --shipment-timeline-gap: 17.5px;
  --shipment-timeline-item-date-width: 200px;
  --shipment-timeline-item-background-color: var(--p-gray-200);
  --shipment-timeline-item-marker-size: 14px;
  --shipment-timeline-item-milestone-marker-size: calc(
    var(--shipment-timeline-item-marker-size) * 2
  );
  --shipment-timeline-item-header-box-height: 43.5px;
  --shipment-timeline-item-milestone-header-box-height: 65.7px;
}

.shipment-timeline.p-timeline.p-timeline-vertical {
  gap: var(--shipment-timeline-gap);

  .p-timeline-event-opposite {
    display: none;
  }

  .p-timeline-event {
    min-height: 0px !important;
    display: flex;
    flex-flow: row nowrap;
    gap: var(--sp-6);
    align-items: center;

    .p-timeline-event-separator {
      min-width: min(
        var(--shipment-timeline-item-marker-size),
        var(--shipment-timeline-item-milestone-marker-size)
      );
      margin: 0;
      flex-basis: var(--shipment-timeline-item-milestone-marker-size);

      .timeline-event-marker-custom-wrapper {
        height: var(--shipment-timeline-item-header-box-height);
        display: flex;
        flex-flow: column;
        justify-content: center;

        &.is-milestone {
          --shipment-timeline-item-marker-size: var(
            --shipment-timeline-item-milestone-marker-size
          );
          --shipment-timeline-item-header-box-height: var(
            --shipment-timeline-item-milestone-header-box-height
          );
        }

        &.is-resolved {
          & + .p-timeline-event-connector {
            --shipment-timeline-item-background-color: var(--success-color);
          }

          --shipment-timeline-item-background-color: var(--success-color);
        }

        &.is-late {
          & + .p-timeline-event-connector {
            --shipment-timeline-item-background-color: var(--warning-color);
          }

          --shipment-timeline-item-background-color: var(--warning-color);
        }

        &.is-critical {
          & + .p-timeline-event-connector {
            --shipment-timeline-item-background-color: var(--danger-color);
          }

          --shipment-timeline-item-background-color: var(--danger-color);
        }

        & + .p-timeline-event-connector {
          background-color: var(--shipment-timeline-item-background-color);
        }

        .timeline-event-marker-custom {
          position: relative;
          display: flex;
          justify-content: center;
          align-items: center;
          height: var(--shipment-timeline-item-marker-size);
          width: var(--shipment-timeline-item-marker-size);
          color: #ffffff;
          background-color: var(--shipment-timeline-item-background-color);
          box-sizing: content-box;
          border: calc(var(--shipment-timeline-gap) / 2) solid #ffffff;
          z-index: 1;
        }
      }

      .p-timeline-event-connector {
        position: absolute;
        top: calc(var(--shipment-timeline-gap) * 2);
        bottom: calc(var(--shipment-timeline-gap) * -2);
        width: var(--sp-1);
        z-index: 0;
      }
    }

    .p-timeline-event-content {
      padding: 0;
    }
  }
}
</style>
